import { Button, Flex, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay } from "@chakra-ui/react";
import { useAppDispatch, useAppSelector } from "../../store";
import { useEffect, useMemo } from "react";
import { ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import { Recruiter } from "@/model/recruiters";
import { BaseTable } from "@/components/BaseTable/BaseTable";
import { fetchRecruitersByDepartment } from "@/store/slices/vacancy";
import { addRecruiter, fetchSingleDepartment } from "@/store/slices/departments";
import { useParams } from "react-router-dom";

interface AddRecruiterModalProps {
  isOpen: boolean;
  onClose: () => void;
  departmentId?: number;
}

const columnAddHelper = createColumnHelper<Recruiter>();

export const AddRecruiterModal = ({ isOpen, onClose, departmentId }: AddRecruiterModalProps) => {
  const dispatch = useAppDispatch();
  const { recruitersByDepartment, isLoading } = useAppSelector((state) => state.vacancy);
  const { id } = useParams();
  const effectiveDepartmentId = departmentId ?? (id ? Number(id) : undefined);

  const ActionCell = (info: { row: Row<Recruiter> }) => {
    const handleMoveRecruiter = (recruiterId: number) => {
      if (!effectiveDepartmentId) return;
      dispatch(
        addRecruiter({
          param: {
            department_id: Number(effectiveDepartmentId),
          },
          data: {
            recruiter_id: recruiterId,
          },
        }),
      ).finally(() => {
        dispatch(fetchSingleDepartment(Number(effectiveDepartmentId)));
        dispatch(fetchRecruitersByDepartment({ department_id: null }));
      });
    };
    return (
      <Button variant="outline" size="sm" onClick={() => handleMoveRecruiter(info.row.original.id)}>
        Добавить
      </Button>
    );
  };

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchRecruitersByDepartment({ department_id: null }));
    }
  }, [dispatch, isOpen]);

  const columns = useMemo<Array<ColumnDef<Recruiter, any>>>(
    () => [
      columnAddHelper.accessor("username", {
        header: "Сотрудник",
        cell: (info) => info.getValue(),
        enableColumnFilter: true,
        enableSorting: true,
      }),
      columnAddHelper.accessor("job_title", {
        header: "Должность",
        cell: (info) => info.getValue(),
        enableColumnFilter: true,
        enableSorting: true,
      }),
      columnAddHelper.accessor("email", {
        header: "Контакты",
        cell: (info) => info.getValue(),
        enableColumnFilter: true,
        enableSorting: true,
      }),
      columnAddHelper.display({
        id: "action",
        header: "Действия",
        cell: ActionCell,
      }),
    ],
    [ActionCell],
  );
  return (
    <Modal isCentered isOpen={isOpen} onClose={onClose}>
      <ModalOverlay backdropFilter="blur(3px)" />
      <ModalContent maxW="70vw" w="70vw" gap="24px" px="24px" paddingTop="32px" paddingBottom="24px">
        <ModalHeader p={0}>Список рекрутеров без подразделения</ModalHeader>
        <ModalBody p={0} pt="8px">
          <Flex maxH="60vh" overflowY="auto">
            <BaseTable list={recruitersByDepartment} columns={columns} isLoading={isLoading} />
          </Flex>
        </ModalBody>
        <ModalFooter p={0} pt="8px">
          <Flex gap="12px" ml="auto">
            <Button variant="outline" onClick={onClose}>
              Закрыть
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
